/**
 * Plan prices and the currency they are shown in.
 *
 * Turkish is shown in TRY, every other language in USD. Sales run through
 * Paratika in TRY today, so the TRY column is what a customer is charged; the
 * USD column is the list price for everyone reading the site in another
 * language. The two are set side by side rather than converted on the fly so
 * a price never lands on an odd number like ₺1.926.
 *
 * Mirrors apps/backend/src/config/plans.ts (base_monthly_try / *_cents).
 */
import type { Language } from "../locales";
import type { PlanTier } from "../hooks/usePlan";

export type Currency = "TRY" | "USD";

type Amount = Record<Currency, number>;

/**
 * TCMB USD selling rate the TRY prices were set from (2026-10-14). Only used
 * for amounts that exist in USD alone — what the AI costs us, and the ROI
 * page's dollar-based value assumptions — never for a price.
 */
export const USD_TRY_RATE = 49.39;

export function currencyFor(language: Language): Currency {
  return language === "tr" ? "TRY" : "USD";
}

/** Monthly base fee; null = negotiated (Enterprise). */
export const PLAN_BASE_PRICE: Record<PlanTier, Amount | null> = {
  FREE: { TRY: 0, USD: 0 },
  STARTER: { TRY: 1899, USD: 39 },
  PRO: { TRY: 4399, USD: 89 },
  GROWTH: { TRY: 13799, USD: 279 },
  ENTERPRISE: null,
};

/** Per AI action over the included quota (0 = no overage on this tier). */
export const OVERAGE_PER_ACTION: Record<PlanTier, Amount> = {
  FREE: { TRY: 0, USD: 0 },
  STARTER: { TRY: 6, USD: 0.12 },
  PRO: { TRY: 3, USD: 0.06 },
  GROWTH: { TRY: 0, USD: 0 },
  ENTERPRISE: { TRY: 0, USD: 0 },
};

const ZERO: Amount = { TRY: 0, USD: 0 };

export const OUTCOME_PRICE: Record<PlanTier, { handled: Amount; lead: Amount; sale: Amount }> = {
  FREE: { handled: ZERO, lead: ZERO, sale: ZERO },
  STARTER: { handled: ZERO, lead: ZERO, sale: ZERO },
  PRO: { handled: ZERO, lead: { TRY: 75, USD: 1.5 }, sale: ZERO },
  GROWTH: { handled: { TRY: 12, USD: 0.25 }, lead: { TRY: 99, USD: 2 }, sale: { TRY: 249, USD: 5 } },
  ENTERPRISE: { handled: ZERO, lead: ZERO, sale: ZERO },
};

/** ₺1.899 / $39 — whole amounts without decimals, the rest with two. */
export function formatMoney(amount: number, currency: Currency): string {
  const rounded = Math.round(amount * 100) / 100;
  const digits = Number.isInteger(rounded) ? 0 : 2;
  return new Intl.NumberFormat(currency === "TRY" ? "tr-TR" : "en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(rounded);
}

/** An amount that only exists in USD, shown in `currency`. */
export function fromUsd(usd: number, currency: Currency): number {
  return currency === "TRY" ? usd * USD_TRY_RATE : usd;
}

export interface BillInput {
  overageActions: number;
  conversationsHandled?: number;
  leadsQualified?: number;
  salesAssisted?: number;
}

/** This period's estimated bill, from counts, in the given currency. */
export function estimateBill(tier: PlanTier, input: BillInput, currency: Currency) {
  const base = PLAN_BASE_PRICE[tier]?.[currency] ?? 0;
  const overage = input.overageActions * OVERAGE_PER_ACTION[tier][currency];
  const o = OUTCOME_PRICE[tier];
  const outcome =
    (input.conversationsHandled ?? 0) * o.handled[currency] +
    (input.leadsQualified ?? 0) * o.lead[currency] +
    (input.salesAssisted ?? 0) * o.sale[currency];
  return { base, overage, outcome, total: base + overage + outcome };
}
